import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useHasLicense } from "../contexts/LicenseContext";
import { useRBAC } from "../hooks/useRBAC";
import { useSocQueueData } from "../hooks/useSocQueueData";
import { useSocTrends } from "../hooks/useSocTrends";
import { useSocTopExposed } from "../hooks/useSocTopExposed";
import { useAuth } from "../contexts/AuthContext";
import { useShowAiIntelligence, useAiIntelPreview } from "../contexts/FeatureFlagsContext";
import SocIncidentQueue from "./SocIncidentQueue";
import Navigation from "./Navigation";
import Card from "./Card";
import SocTile from "./SocTile";
import { SocIssuesByArea, SocSeverityDonut } from "./SocOverviewCharts";
import { RANGES } from "./SocTrendsSection";
import {
  SocExternalActivityChart,
  SocIdentityExposureChart,
  SocNewFindingsChart,
  SocOpenedResolvedChart,
} from "./SocTrendCharts";
import { SOC_TILES } from "./socTileConfig";
import { isConnected, parseUtc } from "./socFreshness";
import { FOCUS_RING, buttonClass, scrollToId } from "./socUi";
import { SocConnectorsPanel, SocDataChecks, SocStaleBanner, stoppedConnectorLines } from "./SocFreshnessPanels";
import { buildMoved } from "./socMoved";
import SocWhatMoved from "./SocWhatMoved";
import SocSideNav from "./SocSideNav";
import SocAreaView from "./SocAreaView";
import SocVulnerabilitiesSection from "./SocTopVulnerabilities";
import SocExposedDetails from "./SocExposedDetails";
import SocKpiBand from "./SocKpiBand";
import { SocMitreTactics, SocPostureGauges, SocPostureRibbon } from "./SocPosture";
import { SocArrivalHeatmap, SocNewestFeed, SocQueueAgeChart, SocTopExposed } from "./SocQueueInsights";
import { areaIssueCount, areaTrend, postureOf } from "./socConsole";
import { trendSources } from "./socTrendSources";
import { buildNav, resolveView } from "./socNav";

// Automatic refresh: summary, trends and queue, silently, while the page is visible.
const AUTO_REFRESH_MS = 60 * 1000;
const HIGHLIGHT_MS = 2500;

const readOrgId = () => {
  try {
    return JSON.parse(localStorage.getItem("selectedOrganization") || "{}")?.organizationId ?? null;
  } catch {
    return null;
  }
};

// A trends-backed chart: a placeholder while the first trends response loads, a short "unavailable"
// when it failed (the page shows one notice with Retry above the charts), else the chart.
const TrendSlot = ({ trends, children }) => {
  if (trends === undefined) {
    return (
      <Card className="h-full">
        <div data-testid="soc-trends-loading" className="flex h-56 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-border border-t-accent-cyan" />
        </div>
      </Card>
    );
  }
  if (trends === null) {
    return (
      <Card className="h-full">
        <p className="text-sm text-content-muted">This chart is unavailable while trends can't be loaded.</p>
      </Card>
    );
  }
  return children;
};

// Security Operations (/soc): an operations console over data Axaris already collects, organized by
// SOC domain. Summary, trends and the incident queue load independently, so one failing never blanks
// the others, and refresh silently every minute while the page is visible.
const SocDashboard = () => {
  // All hooks run before any early return (the rules-of-hooks lint is off in this repo).
  const apiCall = useApi();
  const hasLicense = useHasLicense();
  const { canManageIntegrations, canManageOrganizationRisks, canRunAgent } = useRBAC();
  const { user } = useAuth();
  const showAiIntelligence = useShowAiIntelligence();
  const aiIntelPreview = useAiIntelPreview();
  const orgId = readOrgId();
  // What a tile link may open. The Data tile's leak list needs the AI module on (AI Intelligence
  // redirects to the dashboard otherwise), the AI Intelligence preview on (the Data leaks tab is a
  // preview surface, Features:AiIntelPreview) and an org-wide viewer, the list's own gate.
  const linkAccess = {
    canOpenAiDataLeaks: showAiIntelligence && aiIntelPreview && !!orgId && canRunAgent(orgId),
  };
  const licensed = hasLicense("GRC");

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // null | "forbidden" | "failed"
  const [days, setDays] = useState(30);
  // The queue area chosen in What moved; the incident queue filters on it.
  const [queueArea, setQueueArea] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pollKey, setPollKey] = useState(0);
  // Defender XDR rows are queued only while the summary counts them (its Incidents tile is connected);
  // before the first summary there is nothing to go on, so they are shown.
  const defenderConnected = !summary || isConnected((summary.tiles || []).find((t) => t?.key === "incidents"));
  const queue = useSocQueueData({ enabled: licensed, defenderConnected });
  const { trends, reload: reloadTrends } = useSocTrends({ days, reloadKey, pollKey, enabled: licensed });
  // Ranked lists are read only when their inventory is connected (identity / endpoint tiles).
  const summaryTiles = summary?.tiles || [];
  const inventoryHas = (tileKey, metricKey) => {
    const t = summaryTiles.find((x) => x?.key === tileKey);
    return !!t && t.connected !== false && t.status !== "notConnected"
      && Object.prototype.hasOwnProperty.call(t.metrics || {}, metricKey);
  };
  const wantIdentities = licensed && inventoryHas("identity", "identities");
  const wantDevices = licensed && inventoryHas("endpoint", "devices");
  const topExposed = useSocTopExposed({ identities: wantIdentities, devices: wantDevices, reloadKey, pollKey });
  // Why a ranked list is blank: not connected, still loading, unreadable, or nothing to rank.
  const topEmptyText = (wanted, rows, noun) => {
    if (!wanted) return `The ${noun} inventory isn't connected.`;
    if (rows === undefined) return "Loading…";
    if (rows === null) return `The ${noun} list couldn't be loaded.`;
    return `No ${noun} has open findings.`;
  };
  // The left menu's view, kept in the URL so /soc opens the Overview and each view can be linked to.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewKey = searchParams.get("view") || "overview";
  const selectView = (key) => {
    setSearchParams(key === "overview" ? {} : { view: key });
    scrollToId("soc-content");
  };
  // A figure on an Overview tile opens that area's view with the figure selected (?show=key).
  const openFigure = (viewKeyToOpen, metricKey) => {
    setSearchParams({ view: viewKeyToOpen, show: metricKey });
    scrollToId("soc-content");
  };

  // The automatic refresh in progress: the badge says "Refreshing…" rather than show an old time as
  // current, and the next tick is skipped while it runs (a summary can take longer than a minute), as
  // it is while a user-started load runs. Only the latest request's answer is used: an older response
  // landing late never overwrites a newer one.
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const loadSeqRef = useRef(0);
  const foregroundRef = useRef(0);

  const load = useCallback(async (silent = false) => {
    if (silent && (refreshingRef.current || foregroundRef.current > 0)) return;
    loadSeqRef.current += 1;
    const seq = loadSeqRef.current;
    if (!silent) {
      foregroundRef.current += 1;
      setLoading(true);
      setError(null);
    } else {
      refreshingRef.current = true;
      setRefreshing(true);
    }
    try {
      // The endpoint's 403 (Forbid, no body) is not an "organization" message, so useApi rethrows it
      // to us without redirecting. Only the silent automatic refresh suppresses the auth redirect
      // (background polling); a user-started load keeps it, so a genuine 401 still re-authenticates.
      const data = await apiCall("GET", "/soc/summary", null, false, null, silent);
      if (seq === loadSeqRef.current) setSummary(data || null);
    } catch (err) {
      // A silent failure keeps the figures on screen; the next tick or Refresh tries again.
      if (!silent && seq === loadSeqRef.current) {
        setError(err?.response?.status === 403 ? "forbidden" : "failed");
        setSummary(null);
      }
    } finally {
      if (!silent) {
        foregroundRef.current -= 1;
        if (foregroundRef.current === 0) setLoading(false);
      } else {
        refreshingRef.current = false;
        setRefreshing(false);
      }
    }
  }, [apiCall]);

  // Keyed on the selected organization, like the other dashboards keyed on apiCall: switching
  // organizations remounts the page with a new org id and refetches.
  useEffect(() => {
    if (!licensed) return;
    load();
  }, [licensed, orgId, load]);

  useEffect(() => {
    if (!licensed) return undefined;
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      setPollKey((k) => k + 1);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [licensed]);

  const queueReload = queue.reload;
  useEffect(() => {
    if (pollKey === 0) return;
    load(true);
    queueReload({ silent: true });
  }, [pollKey, load, queueReload]);

  // A KPI card asking the queue to show something: area + tab, then scroll to it. `key` makes a repeat
  // click re-apply the same view after the user changed the queue's filters.
  const [queueRequest, setQueueRequest] = useState(null);
  const showInQueue = ({ area, tab }) => {
    setQueueArea(area);
    setQueueRequest({ tab, key: Date.now() });
    scrollToId("soc-queue");
  };
  // The feed and the posture ribbon open one item's details in the queue.
  const [openRequest, setOpenRequest] = useState(null);
  const openInQueue = (row) => {
    setOpenRequest({ id: row.id, key: Date.now() });
    scrollToId("soc-queue");
  };

  // A device or identity from the Most exposed cards opens under the cards, in SOC (not Security
  // Insights); the same one again closes it.
  const [exposedTarget, setExposedTarget] = useState(null);
  const openExposed = (kind, id) => {
    setExposedTarget((cur) => (cur && cur.kind === kind && String(cur.id) === String(id) ? null : { kind, id }));
    setTimeout(() => scrollToId("soc-exposed-panel"), 0);
  };

  // A posture chip highlights its areas for a moment and scrolls to them.
  const [highlight, setHighlight] = useState(null);
  const highlightTimer = useRef(null);
  useEffect(() => () => clearTimeout(highlightTimer.current), []);
  const jumpToPosture = (posture) => {
    setHighlight(posture);
    scrollToId("soc-areas");
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlight(null), HIGHLIGHT_MS);
  };

  const refresh = () => {
    load();
    queue.reload();
    setReloadKey((k) => k + 1);
  };

  const tiles = useMemo(
    () => (summary?.tiles || []).filter((t) => t && SOC_TILES[t.key]),
    [summary]
  );

  const canConnect = !!canManageIntegrations(orgId);
  const canManage = !!canManageOrganizationRisks?.(orgId);
  const updatedAt = parseUtc(summary?.generatedAt);
  const isOverview = resolveView(viewKey, tiles).key === "overview";

  let body;
  if (!licensed) {
    body = (
      <Card>
        <p className="text-content-secondary">Unified Security Operations isn't included in your organization's plan.</p>
      </Card>
    );
  } else if (loading && !summary) {
    body = (
      <div data-testid="soc-loading" className="flex justify-center py-16">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-accent-cyan" />
      </div>
    );
  } else if (error === "forbidden") {
    body = (
      <Card>
        <p className="text-content-secondary">You don't have access to Unified Security Operations for this organization.</p>
      </Card>
    );
  } else if (error) {
    body = (
      <Card>
        <p className="text-content-secondary">Unified Security Operations couldn't be loaded.</p>
        <button
          type="button"
          onClick={() => load()}
          className="mt-3 inline-flex items-center rounded-md border border-border bg-theme-secondary px-3 py-1.5 text-sm font-medium text-content-primary hover:border-border-hover"
        >
          Retry
        </button>
      </Card>
    );
  } else {
    const incidents = tiles.find((t) => t.key === "incidents");
    const incidentsConnected = incidents && incidents.status !== "notConnected" && incidents.connected !== false;
    const openIncidents = incidentsConnected
      ? (incidents.metrics?.openIncidents || 0) + (incidents.metrics?.openAlerts || 0)
      : 0;
    // One clock for the whole render, so the banner, badges, panel and checks always agree.
    const now = new Date();
    // Data connectors' badge: the stopped connections the banner lists.
    const staleCount = stoppedConnectorLines(tiles, now).length;
    // Menu badges are the areas' open issues, the figure the Overview chart and each tile lead with.
    const issuesOf = (key) => areaIssueCount(tiles.find((t) => t.key === key)) || 0;
    const externalOpen = issuesOf("external");
    const sources = trendSources(tiles);
    const showFindings = sources.defender || sources.externalRisks;

    const view = resolveView(viewKey, tiles);
    const aiRisks = issuesOf("agenticAi");
    const nav = buildNav(tiles, {
      agenticAi: aiRisks > 0 ? aiRisks : null,
      incidents: incidentsConnected && openIncidents > 0 ? openIncidents : null,
      external: externalOpen > 0 ? externalOpen : null,
      connectors: staleCount > 0 ? staleCount : null,
    });

    const overview = (
      <>
        <SocStaleBanner tiles={tiles} now={now} canConnect={canConnect} />
        <SocPostureRibbon tiles={tiles} now={now} rows={queue.rows} onJump={jumpToPosture} onUrgent={openInQueue} />
        {trends === null && (
          <div role="status" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-status-warning bg-status-warning-bg px-4 py-2.5 text-sm">
            <span className="text-content-primary">Trends couldn't be loaded.</span>
            <button type="button" onClick={reloadTrends} className={buttonClass("sm")}>Retry</button>
          </div>
        )}
        <SocKpiBand
          tiles={tiles}
          incidents={incidents}
          trends={trends}
          queue={queue}
          exposure={summary?.exposure}
          now={now}
          onIncidents={() => showInQueue({ area: "incidents", tab: "all" })}
          onUnassigned={() => showInQueue({ area: null, tab: "unassigned" })}
          onConnectors={() => scrollToId("soc-connectors")}
        />

        <section aria-label="Overview" className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-12">
          {/* Every chart keeps its place; a chart without a source or data draws as a blank frame
              that says why, so the layout stays whole and no gap reads as zero. */}
          <div className="min-w-0 lg:col-span-2 xl:col-span-8">
            <TrendSlot trends={trends}>
              <SocNewFindingsChart
                rangeDays={days}
                points={trends?.newFindings || []}
                showDefender={sources.defender}
                showExternal={sources.externalRisks}
                height="h-64"
                emptyText={showFindings ? undefined : "No Defender XDR or external threat source is connected."}
              />
            </TrendSlot>
          </div>
          <div className="min-w-0 xl:col-span-4">
            <SocSeverityDonut
              tile={incidentsConnected ? incidents : null}
              emptyText={incidentsConnected ? undefined : "Defender XDR isn't connected."}
            />
          </div>
          <div className="min-w-0 xl:col-span-4">
            <SocIssuesByArea tiles={tiles} />
          </div>
          <div className="min-w-0 xl:col-span-4">
            <TrendSlot trends={trends}>
              <SocExternalActivityChart
                rangeDays={days}
                points={trends?.externalEvents || []}
                bands={sources.bands}
                emptyText={sources.bands.length > 0 ? undefined : "No external threat source is connected."}
              />
            </TrendSlot>
          </div>
          <div className="min-w-0 xl:col-span-4">
            <SocPostureGauges tiles={tiles} />
          </div>
          <div className="min-w-0 xl:col-span-5">
            <SocWhatMoved
              items={buildMoved(tiles, trends, now)}
              trends={trends}
              selectedArea={queueArea}
              onSelectArea={setQueueArea}
              onOpenView={selectView}
            />
          </div>
          <div className="min-w-0 lg:col-span-2 xl:col-span-7">
            <SocArrivalHeatmap rows={queue.rows} />
          </div>
          <div className="min-w-0 lg:col-span-2 xl:col-span-12">
            <SocMitreTactics
              tactics={summary?.tactics || []}
              emptyText={incidentsConnected ? "No tactic data yet." : "Defender XDR isn't connected."}
            />
          </div>
        </section>

        <section aria-label="Areas" id="soc-areas" className="mb-4 grid scroll-mt-4 grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="min-w-0 xl:col-span-8">
            <h2 className="mb-3 text-lg font-semibold text-content-primary">Areas</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
              {tiles.map((tile) => (
                <SocTile
                  key={tile.key}
                  tile={tile}
                  config={SOC_TILES[tile.key]}
                  canConnect={canConnect}
                  now={now}
                  trend={areaTrend(tile.key, trends)}
                  highlight={!!highlight && postureOf(tile, now) === highlight}
                  linkAccess={linkAccess}
                  onMetric={SOC_TILES[tile.key].clickable ? (metricKey) => openFigure(tile.key, metricKey) : null}
                />
              ))}
            </div>
          </div>
          <div className="min-w-0 xl:col-span-4 xl:pt-10">
            <SocNewestFeed rows={queue.rows} onOpen={openInQueue} />
          </div>
        </section>

        <section aria-label="Trends" className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {/* Always shown: the page already requires the GRC license. */}
          <TrendSlot trends={trends}>
            <SocOpenedResolvedChart
              testId="soc-chart-control-alerts"
              title="Control alerts raised vs resolved"
              subtitle="GRC control regressions and their recoveries"
              points={trends?.controlAlerts || []}
              openedLabel="Raised"
              resolvedLabel="Resolved"
              emptyText="No control alerts in this period."
            />
          </TrendSlot>
          <TrendSlot trends={trends}>
            <SocOpenedResolvedChart
              testId="soc-chart-network"
              title="Network signals opened vs resolved"
              subtitle="Fortinet firewall signals"
              points={sources.network ? trends?.networkSignals || [] : []}
              openedLabel="Opened"
              resolvedLabel="Resolved"
              emptyText={sources.network ? "No network signals in this period." : "Fortinet isn't connected."}
            />
          </TrendSlot>
          <TrendSlot trends={trends}>
            <SocIdentityExposureChart
              points={sources.identity ? trends?.identityExposure || [] : []}
              emptyText={sources.identity ? undefined : "The identity inventory isn't connected."}
            />
          </TrendSlot>
        </section>

        <section aria-label="Exposure" className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <SocTopExposed kind="identities" rows={topExposed.identities}
            emptyText={topEmptyText(wantIdentities, topExposed.identities, "identity")}
            openId={exposedTarget?.kind === "identity" ? exposedTarget.id : null}
            onOpen={(r) => openExposed("identity", r.id)} />
          <SocTopExposed kind="devices" rows={topExposed.devices}
            emptyText={topEmptyText(wantDevices, topExposed.devices, "device")}
            openId={exposedTarget?.kind === "device" ? exposedTarget.id : null}
            onOpen={(r) => openExposed("device", r.id)} />
          <SocQueueAgeChart rows={queue.rows} />
        </section>
        {exposedTarget && (
          <div id="soc-exposed-panel" className="mb-4 scroll-mt-4">
            <SocExposedDetails
              key={`${exposedTarget.kind}-${exposedTarget.id}`}
              target={exposedTarget}
              organizationId={orgId}
              onClose={() => setExposedTarget(null)}
              onSwitch={(next) => setExposedTarget(next)}
              riskDetailsKey={queue.changeKey}
            />
          </div>
        )}

        <SocVulnerabilitiesSection
          vulnerabilities={summary?.vulnerabilities ?? null}
          endpointTile={tiles.find((t) => t.key === "endpoint")}
          now={now}
          riskDetailsKey={queue.changeKey}
        />

        <div className="mb-6">
          <SocIncidentQueue
            queue={queue}
            area={queueArea}
            onClearArea={() => setQueueArea(null)}
            request={queueRequest}
            openRequest={openRequest}
            userId={user?.id ?? null}
            canManage={canManage}
          />
        </div>
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SocConnectorsPanel tiles={tiles} now={now} />
          <SocDataChecks tiles={tiles} now={now} />
        </div>
      </>
    );

    body = (
      <div className="lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-6">
        <SocSideNav groups={nav} active={view.key} onSelect={selectView} />
        <div id="soc-content" className="min-w-0 scroll-mt-4">
          {view.key === "overview" ? overview : (
            <SocAreaView
              view={view}
              tiles={tiles}
              vulnerabilities={summary?.vulnerabilities ?? null}
              linkAccess={linkAccess}
              initialMetric={searchParams.get("show")}
              now={now}
              canConnect={canConnect}
              days={days}
              onDaysChange={setDays}
              reloadKey={reloadKey}
              pollKey={pollKey}
              queue={queue}
              userId={user?.id ?? null}
              canManage={canManage}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-screen-2xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white shadow-[0_8px_18px_-8px_rgba(79,70,229,0.7)]"
              style={{ background: "linear-gradient(135deg, #1e40af, #7c3aed)" }}
              aria-hidden="true"
            >
              <ShieldCheck className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-content-primary sm:text-3xl">Unified Security Operations</h1>
                {/* Drawn, not the beta-badge.svg image: content blockers drop image URLs containing "badge". */}
                <span
                  className="select-none rounded-full border border-violet-300 bg-violet-100 px-2 py-px text-[11px] font-semibold leading-4 text-violet-700"
                  data-testid="soc-beta"
                >
                  Beta
                </span>
              </div>
              <p className="text-sm text-content-secondary">
                Incidents, exposure and posture from your connected security tools.
              </p>
            </div>
          </div>
          {licensed && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-content-muted">
              {updatedAt && (
                <span
                  data-testid="soc-updated"
                  className="inline-flex items-center gap-2 rounded-full border border-status-success bg-status-success-bg px-3 py-1 text-xs font-semibold text-status-success"
                >
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-status-success opacity-60 motion-safe:animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-status-success" />
                  </span>
                  LIVE
                  <span className="font-medium text-content-secondary">
                    {refreshing ? "Refreshing…" : `Updated ${updatedAt.toLocaleTimeString()} · refreshes every minute`}
                  </span>
                </span>
              )}
              {isOverview && (
                <div role="group" aria-label="Trend range" className="inline-flex rounded-lg border border-border bg-theme-card-solid p-0.5">
                  {RANGES.map((range) => (
                    <button
                      key={range}
                      type="button"
                      aria-pressed={range === days}
                      onClick={() => setDays(range)}
                      className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${FOCUS_RING} ${
                        range === days ? "bg-accent-cyan text-white" : "text-content-secondary hover:text-content-primary"
                      }`}
                    >
                      {range} days
                    </button>
                  ))}
                </div>
              )}
              <button type="button" onClick={refresh} disabled={loading} className={buttonClass("md")}>
                <RefreshCw className={`h-4 w-4 ${loading ? "motion-safe:animate-spin" : ""}`} aria-hidden="true" />
                Refresh
              </button>
            </div>
          )}
        </div>
        {body}
        {licensed && (
          <p className="mt-6 border-t border-border pt-3 text-xs leading-relaxed text-content-muted" data-testid="soc-disclaimer">
            Unified Security Operations is in beta. It brings together data from your connected security tools as of each
            tool's last sync, so figures can lag behind the tool itself, and it covers only what Axaris collects (see
            the coverage notes on each area). It does not replace your security tools' own consoles: confirm
            critical findings in the source tool before acting on them.
          </p>
        )}
      </div>
    </div>
  );
};

export default SocDashboard;
